import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import {
  AddViewRequest,
  CustomView,
  SetDefaultViewRequest,
  UpdateViewRequest,
} from './types';

export type {
  AddViewRequest,
  CustomView,
  SetDefaultViewRequest,
  UpdateViewRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const base = `${getBaseUrl()}/api/v1/custom-view`;
  const url = path
    ? params
      ? `${base}/${path}?${params.toString()}`
      : `${base}/${path}`
    : params
      ? `${base}?${params.toString()}`
      : base;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const base = `${getBaseUrl()}/api/v1/custom-view`;
  const url = path ? `${base}/${path}` : base;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (
    response.status === 204 ||
    response.headers.get('content-length') === '0'
  ) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function fetchViewNames(
  token: AccessToken,
  params: {
    username: string;
    usage: string;
    app: string;
  },
): Promise<CustomView[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('username', params.username);
  searchParams.set('usage', params.usage);
  searchParams.set('app', params.app);
  return fetchGet<CustomView[]>(token, '', searchParams);
}

async function fetchViewById(
  token: AccessToken,
  id: string,
): Promise<CustomView> {
  return fetchGet<CustomView>(token, id);
}

async function addViewFn(
  token: AccessToken,
  request: AddViewRequest,
): Promise<CustomView> {
  return fetchMutation<CustomView>(token, '', 'POST', request);
}

async function updateViewFn(
  token: AccessToken,
  request: UpdateViewRequest,
): Promise<void> {
  return fetchMutation<void>(token, request.id, 'PUT', { view: request.view });
}

async function removeViewFn(token: AccessToken, id: string): Promise<void> {
  return fetchMutation<void>(token, id, 'DELETE');
}

async function setDefaultViewFn(
  token: AccessToken,
  request: SetDefaultViewRequest,
): Promise<void> {
  return fetchMutation<void>(token, `${request.id}/default`, 'PUT', {
    username: request.username,
    usage: request.usage,
  });
}

export const VIEW_NAMES_KEY = 'custom-views';

export const viewNamesOptions = (
  token: AccessToken,
  params: {
    username: string;
    usage: string;
    app: string;
  },
) =>
  queryOptions({
    queryKey: [VIEW_NAMES_KEY, 'view-names', params],
    enabled: !!token,
    async queryFn() {
      return fetchViewNames(token, params);
    },
  });

export const viewByIdOptions = (token: AccessToken, id: string) =>
  queryOptions({
    queryKey: [VIEW_NAMES_KEY, 'view-by-id', id],
    enabled: !!token,
    async queryFn() {
      return fetchViewById(token, id);
    },
  });

export function useAddViewRestMutation() {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AddViewRequest) => addViewFn(token, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [VIEW_NAMES_KEY] });
    },
  });
}

export function useUpdateViewRestMutation() {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateViewRequest) => updateViewFn(token, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [VIEW_NAMES_KEY] });
    },
  });
}

export function useRemoveViewRestMutation() {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeViewFn(token, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [VIEW_NAMES_KEY] });
    },
  });
}

export function useSetDefaultViewRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: SetDefaultViewRequest) =>
      setDefaultViewFn(token, request),
  });
}
